import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, DuoColorIcon, Icon, Text } from '@zillow/constellation-10';
import { IconFileFilled, IconSignatureOutline } from '@zillow/constellation-icons';
import ActiveCard from './ActiveCard';
import EmptyCard from './EmptyCard';
import ErrorCard from './ErrorCard';
import { LINKS } from '../../constants';
import type {
  YourRentalErrorDto,
  YourRentalLeaseDto,
  YourRentalLeaseSectionDto,
} from '@zg-rentals/ts-your-rental-api-client';

const CONTENT = {
  EMPTY_TITLE: 'Add your lease',
  EMPTY_BODY:
    'Uploading your lease to Zillow makes accessing it quick and easy. Send your landlord the link below to ask them to upload the lease.',
  EMPTY_CTA: 'Copy link to invite landlord',
} as const;

type LeasesCardProps = {
  leaseSection?: YourRentalLeaseSectionDto;
};

type LeasesActiveCardProps = {
  defaultLeaseIndex: number;
  leases: Array<YourRentalLeaseDto>;
};

const LeasesEmptyCard = () => {
  return (
    <EmptyCard
      icon={
        <DuoColorIcon tone="empower">
          <Icon>
            <IconSignatureOutline />
          </Icon>
        </DuoColorIcon>
      }
      title={CONTENT.EMPTY_TITLE}
      description={CONTENT.EMPTY_BODY}
      link={LINKS.RENTAL_MANAGER_LEASE_AGREEMENTS}
      buttonText={CONTENT.EMPTY_CTA}
      dataTestId="Leases"
      shouldCopyLink={true}
    />
  );
};

const LeasesErrorCard = ({ error }: { error: YourRentalErrorDto }) => {
  return (
    <ErrorCard
      icon={
        <DuoColorIcon tone="info">
          <Icon>
            <IconFileFilled />
          </Icon>
        </DuoColorIcon>
      }
      title={error.cardTitle}
      error={error}
      dataTestId="Leases"
    />
  );
};

const LeasesActiveCard = ({ defaultLeaseIndex, leases }: LeasesActiveCardProps) => {
  const [selectedLease, setSelectedLease] = useState<YourRentalLeaseDto>(leases[defaultLeaseIndex]);

  const leasesByLeaseId = useMemo(() => new Map(leases.map((lease) => [lease.leaseId, lease])), [leases]);
  const leaseOptions = useMemo(
    () => leases.map((lease) => ({ value: lease.leaseId, label: lease.dateText })),
    [leases],
  );

  useEffect(() => {
    setSelectedLease(leases[defaultLeaseIndex]);
  }, [leases, defaultLeaseIndex]);

  const handleLeaseChange = useCallback(
    (leaseIdInput: string) => {
      const selected = leasesByLeaseId.get(leaseIdInput);
      if (selected) {
        setSelectedLease(selected);
      }
    },
    [leasesByLeaseId],
  );

  return (
    <ActiveCard
      title={selectedLease.cardTitle}
      dataTestId="LeasesActiveCard"
      icon={
        <DuoColorIcon tone="info">
          <Icon>
            <IconFileFilled />
          </Icon>
        </DuoColorIcon>
      }
      dropdownOptions={leases.length > 1 ? leaseOptions : undefined}
      dropdownValue={selectedLease.leaseId}
      onDropdownChange={handleLeaseChange}
      dropdownShowLabelForValue
      dropdownAriaLabel="Select lease agreement"
      tags={[selectedLease.statusTag]}
      ctas={[selectedLease.cta]}
    >
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'micro.default',
        }}
      >
        <Text textStyle="body-lg-bold">{selectedLease.dateText}</Text>
        <Text>{selectedLease.tenantsText}</Text>
      </Box>
    </ActiveCard>
  );
};

const LeasesCard = ({ leaseSection }: LeasesCardProps) => {
  const showUpsell = !leaseSection || leaseSection.upsellCardType || leaseSection.leases.length === 0;

  if (leaseSection?.error) {
    return <LeasesErrorCard error={leaseSection.error} />;
  }

  return showUpsell ? (
    <LeasesEmptyCard />
  ) : (
    <LeasesActiveCard defaultLeaseIndex={leaseSection.defaultLeaseIndex || 0} leases={leaseSection.leases} />
  );
};

export default LeasesCard;
